/**
 * The icon row under the name. Every button here DOES the thing.
 *
 * Teams shows an org-chart button in this row. We have no org-chart surface, so
 * there is no button for it — a control that opens nothing is worse than a
 * control that is absent, because it teaches you the card is decorative.
 */
import { MessageSquare, Phone, Video } from 'lucide-react';
import { IconButton, cn } from '@cyphex/ui';
import type { UserId } from '@cyphex/types';
import { usePlaceCall } from '@/features/calls/hooks/usePlaceCall';
import { useOpenDirectMessage } from '@/features/chat/hooks/useOpenDirectMessage';

export interface ProfileQuickActionsProps {
  userId: UserId;
  displayName: string;
  /** Hides chat + call for your own card — you cannot ring yourself. */
  isSelf: boolean;
  /** Lets the popover close itself once an action has navigated away. */
  onNavigate?: () => void;
  className?: string;
}

export function ProfileQuickActions({
  userId,
  displayName,
  isSelf,
  onNavigate,
  className,
}: ProfileQuickActionsProps) {
  const openDm = useOpenDirectMessage();
  const { place, isPending } = usePlaceCall();

  const call = (kind: 'audio' | 'video'): void => {
    place({ calleeIds: [userId], kind, peerName: displayName });
    onNavigate?.();
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <IconButton
        icon={MessageSquare}
        label={isSelf ? 'Open your notes' : `Chat with ${displayName}`}
        size="lg"
        variant="ghost"
        onClick={() => {
          void openDm(userId).then(onNavigate);
        }}
      />
      {isSelf ? null : (
        <>
          <IconButton
            icon={Video}
            label={`Start a video call with ${displayName}`}
            size="lg"
            variant="ghost"
            disabled={isPending}
            onClick={() => call('video')}
          />
          <IconButton
            icon={Phone}
            label={`Start an audio call with ${displayName}`}
            size="lg"
            variant="ghost"
            disabled={isPending}
            onClick={() => call('audio')}
          />
        </>
      )}
    </div>
  );
}
